import { AppLogo } from './AppLogo';

interface DisclosureApp {
  appId: string;
  displayName: string;
  iconUrl?: string | null;
}

/** Above this many apps, app lists start collapsed behind an AppsDisclosure bar. */
export const COLLAPSE_APPS_ABOVE = 3;

interface AppsDisclosureProps {
  apps: DisclosureApp[];
  expanded: boolean;
  onToggle: () => void;
  /** id of the element the bar shows and hides. */
  controlsId: string;
  /** `dark` is the compact two-line form for the narrow dark side panels. */
  tone?: 'light' | 'dark';
  /** The bar's headline; defaults to "N apps ready to switch on". */
  title?: string;
}

/** The ⌄ that turns to ⌃ when a collapsible section is open. */
export function Chevron({ expanded, className }: { expanded: boolean; className: string }) {
  return (
    <svg
      className={`h-4 w-4 flex-none transition-transform ${expanded ? 'rotate-180' : ''} ${className}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/**
 * The one-line summary of a long app list: overlapping logos, a count and the
 * first few names. Clicking it shows or hides the full list, so a long list
 * never pushes the rest of the step off screen.
 */
export function AppsDisclosure({ apps, expanded, onToggle, controlsId, tone = 'light', title }: AppsDisclosureProps) {
  const dark = tone === 'dark';
  const shown = apps.slice(0, 5);
  const rest = apps.length - shown.length;

  const logos = (
    <span className="flex flex-none items-center" aria-hidden="true">
      {shown.map((a, i) => (
        <span
          key={a.appId}
          className={`rounded-xl p-0.5 ${i > 0 ? (dark ? '-ml-2' : '-ml-2.5 sm:-ml-3') : ''} ${
            dark ? 'bg-slate-900' : 'bg-white shadow-sm ring-1 ring-gray-200'
          }`}
        >
          <AppLogo appId={a.appId} displayName={a.displayName} iconUrl={a.iconUrl} size={dark ? 'sm' : 'sm-md'} />
        </span>
      ))}
      {rest > 0 && (
        <span
          className={`flex flex-none items-center justify-center rounded-xl font-semibold ${
            dark
              ? '-ml-2 h-9 w-9 bg-slate-800 text-[11px] text-slate-200 ring-2 ring-slate-900'
              : '-ml-2.5 h-9 w-9 bg-white text-[11px] text-gray-600 shadow-sm ring-1 ring-gray-200 sm:-ml-3 sm:h-12 sm:w-12 sm:text-xs'
          }`}
        >
          +{rest}
        </span>
      )}
    </span>
  );

  if (dark) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={controlsId}
        data-testid="apps-disclosure"
        className="flex w-full flex-col items-stretch gap-2.5 rounded-2xl border border-white/10 bg-white/[0.07] p-3 text-left transition-colors hover:bg-white/[0.12] focus:outline-none focus:ring-2 focus:ring-emerald-400"
      >
        {logos}
        <span className="flex items-center justify-between gap-2 text-xs font-semibold">
          <span className="text-white">{expanded ? 'Hide apps' : `Show all ${apps.length} apps`}</span>
          <Chevron expanded={expanded} className="text-emerald-300" />
        </span>
      </button>
    );
  }

  const named = apps.slice(0, 3).map((a) => a.displayName);
  const more = apps.length - named.length;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controlsId}
      data-testid="apps-disclosure"
      className="flex w-full flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 text-left transition-colors hover:border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 sm:flex-row sm:items-center sm:p-4"
    >
      {logos}
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900">
            {title ?? `${apps.length} apps ready to switch on`}
          </span>
          <span className="mt-0.5 block truncate text-xs text-gray-500">
            {named.join(', ')}
            {more > 0 ? ` and ${more} more` : ''}
          </span>
        </span>
        <span className="flex flex-none items-center gap-1 text-xs font-semibold text-emerald-700 sm:text-sm">
          {expanded ? 'Hide' : 'Show all'}
          <Chevron expanded={expanded} className="" />
        </span>
      </span>
    </button>
  );
}
